var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import clsx from 'clsx';
import { HawkinsPropsOverrideContext, isNil, noopFunction, useClasses, useControlledStateWarning, useLayoutProps, useProps, } from '@hawkins/helpers';
import { TriggerContainer } from '../_private/triggerContainer/triggerContainer';
import { FloatingUI } from '../floatingUi';
import { useCombineRefs, useId } from '../hooks';
import { Paper } from '../paper';
import { usePopoverStyles } from './popover.styles';
var displayName = 'Popover';
var overrideProps = {
    FloatingUI: { disablePortal: true },
};
/**
 * The `Popover` component is a generic component. This component expects you to pass a `trigger`
 * component that will accept an `onClick` prop to trigger the `Popover` to open.
 *
 * The content of the `Popover` is up to you to implement. Make use of other Hawkins
 * components to get the correct layout. `Text` and `Box` go extremely well with
 * `Popover`.
 *
 * The children of the `Popover` component will be rendered inside of the `Popover`.
 *
 * All props, classes, styles and ref will be propagated to the `Popover` component itself.
 */
export var Popover = React.forwardRef(function (_a, ref) {
    var _b, _c;
    var children = _a.children, props = __rest(_a, ["children"]);
    // Props
    var _d = useProps(displayName, props), anchorRef = _d.anchorRef, classesProp = _d.classes, className = _d.className, _e = _d.closeOnEsc, closeOnEsc = _e === void 0 ? true : _e, containerClassNameProp = _d.containerClassName, _f = _d.disabled, disabled = _f === void 0 ? false : _f, _g = _d.disableActiveStyling, disableActiveStyling = _g === void 0 ? false : _g, _h = _d.floatingUiProps, floatingUiProps = _h === void 0 ? {} : _h, _j = _d.keepOpenOnTriggerClick, keepOpenOnTriggerClick = _j === void 0 ? false : _j, _k = _d.useFocusManager, useFocusManager = _k === void 0 ? true : _k, _l = _d.fullWidth, fullWidth = _l === void 0 ? false : _l, idProp = _d.id, layout = _d.layout, _m = _d.onToggle, onToggle = _m === void 0 ? noopFunction : _m, openProp = _d.open, _o = _d.placement, placement = _o === void 0 ? 'bottom-start' : _o, styleProp = _d.style, trigger = _d.trigger, restProps = __rest(_d, ["anchorRef", "classes", "className", "closeOnEsc", "containerClassName", "disabled", "disableActiveStyling", "floatingUiProps", "keepOpenOnTriggerClick", "useFocusManager", "fullWidth", "id", "layout", "onToggle", "open", "placement", "style", "trigger"]);
    useControlledStateWarning(openProp, { displayName: displayName, stringifiedNullValue: 'false' });
    // Styles
    var baseClasses = usePopoverStyles();
    var classes = useClasses(displayName, baseClasses, classesProp);
    var style = useLayoutProps({ layout: layout, style: styleProp });
    var rootClassName = clsx(classes.HAWKINS_BASE, classes.root, classes.paper, className, (_b = {},
        _b[classes.bottom] = placement.includes('bottom'),
        _b[classes.left] = placement.includes('left'),
        _b[classes.right] = placement.includes('right'),
        _b[classes.top] = placement.includes('top'),
        _b));
    var containerClassName = clsx(classes.popoverTriggerContainer, containerClassNameProp, (_c = {},
        _c[classes.popoverTriggerContainerFullWidth] = fullWidth,
        _c));
    // State
    var _p = __read(React.useState(null), 2), triggerNode = _p[0], setTriggerNode = _p[1];
    var internalRef = useCombineRefs([setTriggerNode, trigger.ref]);
    var isControlled = !isNil(openProp);
    var _q = __read(React.useState(false), 2), internalOpen = _q[0], setInternalOpen = _q[1];
    var id = useId(displayName, { defaultId: idProp });
    var open = openProp !== null && openProp !== void 0 ? openProp : internalOpen;
    // Handlers
    var closePopover = React.useCallback(function (event) {
        /**
         * We only close the popover and fire this logic if it's currently open.
         * Otherwise, it doesn't make any sense to open the popover
         * by clicking on any arbitrary DOM element on the page
         *
         * https://jira.netflix.net/browse/HAWK-1040
         */
        if (!open)
            return;
        if (!isControlled)
            setInternalOpen(false);
        onToggle(false, event);
    }, [open, isControlled, onToggle]);
    var handleEscPressed = React.useCallback(function (event) {
        if (closeOnEsc) {
            closePopover(event);
        }
    }, [closeOnEsc, closePopover]);
    // Trigger
    // Enforce only child
    var triggerElement = React.Children.only(trigger);
    var triggerProps = React.useMemo(function () {
        return __assign(__assign({}, triggerElement.props), { 'aria-describedby': open ? id : null, ref: internalRef, onClick: function (event) {
                var _a, _b;
                event.stopPropagation();
                if (disabled)
                    return;
                (_b = (_a = triggerElement.props).onClick) === null || _b === void 0 ? void 0 : _b.call(_a, event);
                if (open && keepOpenOnTriggerClick)
                    return;
                var newOpen = !open;
                if (!isControlled)
                    setInternalOpen(newOpen);
                onToggle(newOpen, event);
            } });
    }, [disabled, id, internalRef, isControlled, keepOpenOnTriggerClick, onToggle, open, triggerElement.props]);
    var triggerToRender = React.cloneElement(triggerElement, triggerProps);
    var activeAnchor;
    if (anchorRef) {
        // type safe check to verify if the passed anchorRef is a virtual element or a reference to a dom node.
        activeAnchor = 'current' in anchorRef ? anchorRef.current : anchorRef;
    }
    return (_jsxs(_Fragment, { children: [_jsx(TriggerContainer, __assign({ disableActiveStyling: disableActiveStyling, className: containerClassName, active: open }, { children: triggerToRender })), _jsx(FloatingUI, __assign({ useFocusManager: useFocusManager, className: rootClassName, disabled: disabled, styled: false, style: style, open: open, ref: ref, anchor: activeAnchor !== null && activeAnchor !== void 0 ? activeAnchor : triggerNode, placement: placement, onOutsideClick: closePopover, onEscPressed: closeOnEsc ? handleEscPressed : undefined, id: id }, restProps, floatingUiProps, { children: _jsx(Paper, __assign({ className: classes.paper, elevation: 2 }, { children: _jsx(HawkinsPropsOverrideContext.Provider, __assign({ value: overrideProps }, { children: children })) })) }))] }));
});
Popover.displayName = displayName;
